import moment from 'moment';
import { uniqBy } from 'lodash-es';
import { isNullOrUndefined, isEmptyList, addId, addIdBatch } from '@gp/utils/common';
import { getIsInitialList, getIsInitialActionItems } from '../../../utils';

import type { IRecord } from '@gp/types/global';
import type { IUpdateVO, IActionItemVO, IRiskVO } from '@gp/types/projectUpdate';

interface ISynchronizeParams {
  granularity?: number;
  date?: string;
  list?: IUpdateVO[] | null;
}

/**
 * Action Items继承的开始日期，业务规定只继承2024-10-01及之后的日期的Updates的Action Items数据（Not Started、In Progress、On hold）
 * 因为Updates的Action Items继承功能是在2024-11-01之后上线的，所以仅从2024-10-01及其之后的Updates开始继承，之前的用户已经手动同步过了，不需要再继承了
 */
const ACTION_ITEMS_INHERIT_START_DATE = '2024-10-01';

/**
 * 可继承Action Items的状态
 * 1: Not Started
 * 2: In progress
 * 3: On hold
 */
const ACTION_ITEMS_INHERIT_STATUS = ['1', '2', '3'];

/**
 * 解析Updates的日期，返回标准日期字符串元组
 * 异常时，返回['', '']
 * @param granularity 时间粒度
 * @param date 日期字符串，2024.01、2024.10.13-2024.10.19
 * @returns 标准日期字符串元组 [2024-01-01, 2024-01-31]、[2024-10-13, 2024-10-19]
 */
const analysisUpdatesDate = (granularity?: number, date?: string): [string, string] => {
  if (isNullOrUndefined(granularity)) return ['', ''];
  if (!date) return ['', ''];
  const dateTmp = date.replaceAll('-', '~')?.replaceAll('.', '-');
  // 月粒度
  if (granularity === 1) {
    const monthDate = `${dateTmp}-01`;
    return [monthDate, moment(monthDate).endOf('month').format('YYYY-MM-DD')];
  }
  // 周粒度
  if (granularity === 2) return dateTmp.split('~') as [string, string];
  return ['', ''];
};

/**
 * 获取指定update的前一个updates
 * @param params 参数对象
 * @param params.granularity 时间粒度
 * @param params.date 日期字符串，2024.01、2024.10.13-2024.10.19
 * @param params.list Updates列表
 * @returns 指定update的前一个updates
 */
const findPreviousUpdate = (params?: ISynchronizeParams): IUpdateVO | null => {
  const { granularity, date, list } = params || {};

  if (!Array.isArray(list) || !list.length) return null;
  if (!date) return null;

  const [, endDate] = analysisUpdatesDate(granularity, date);
  if (!endDate) return null;
  const endDateM = moment(endDate);

  for (const item of list) {
    const { granularity: itemGranularity, date: itemDate } = item || {};
    const [, itemEndDate] = analysisUpdatesDate(itemGranularity, itemDate);
    const itemEndDateM = moment(itemEndDate);

    if (granularity === itemGranularity && date === itemDate) continue;
    // 当前Updates的结束日期在指定Updates的结束日期之后，则不继承
    if (itemEndDateM.isAfter(endDateM)) continue;
    // 当前Updates的结束日期在允许继承的开始日期之前，则不继承
    if (itemEndDateM.isBefore(moment(ACTION_ITEMS_INHERIT_START_DATE))) continue;
    return item;
  }

  return null;
};

/**
 * 计算指定Updates能继承的Action Items数据
 * 继承规则：
 * 1、时间范围: [ACTION_ITEMS_INHERIT_START_DATE, 指定Updates的结束日期]，注意是闭区间
 * 2、仅继承状态为ACTION_ITEMS_INHERIT_STATUS中的Action Items数据
 * @param params 入参，详情参考ISynchronizeParams类型定义
 * @returns 指定Updates能继承的Action Items数据
 */
export const synchronizeActionItems = (params?: ISynchronizeParams): IActionItemVO[] => {
  const previousUpdate = findPreviousUpdate(params);
  const { workplans } = previousUpdate?.content || {};
  if (!Array.isArray(workplans) || !workplans.length) return [];
  return workplans
    .filter((workplan) => {
      const { status } = workplan || {};
      return ACTION_ITEMS_INHERIT_STATUS.includes(status);
    })
    .map(addId);
};

export const synchronizeRisks = (params?: ISynchronizeParams): IRiskVO[] => {
  const previousUpdate = findPreviousUpdate(params);
  return addIdBatch<IRiskVO>(previousUpdate?.content?.new_risks) || [];
};

/**
 * 对aciton items集合去重
 * 去重的标准：action item的comment相同即判定为相同的action item，并且后面的action item会覆盖前面相同的action item
 * @param actionItems action items集合
 * @returns 去重后的action items集合
 */
const uniqActionItems = (actionItems?: IActionItemVO[] | null): IActionItemVO[] => {
  if (!Array.isArray(actionItems) || !actionItems.length) return [];
  const result: IActionItemVO[] = [];
  const actionItemIndexs: IRecord<number> = {};

  for (const actionItem of actionItems) {
    const { comment } = actionItem || {};
    const actionItemIndex = actionItemIndexs[comment];
    if (actionItemIndex === undefined) {
      const resultLength = result.push(actionItem);
      actionItemIndexs[comment] = resultLength - 1;
      continue;
    }

    result[actionItemIndex] = {
      ...result[actionItemIndex],
      ...actionItem,
    };
  }

  return result;
};

/**
 * 获取组合后的Action Items
 * @param currentActionItems 当前Updates的Action Items
 * @param synchronizedActionItems 当前Updates的继承的Action Items
 * @returns 组合后的Action Items
 */
export const composeActionItems = (
  currentActionItems?: IActionItemVO[] | null,
  synchronizedActionItems?: IActionItemVO[] | null,
): IActionItemVO[] => {
  if (!currentActionItems && !synchronizedActionItems) return [];
  const isCurrentRisksEmpty = isEmptyList(currentActionItems);
  const isSynchronizedRisksEmpty = isEmptyList(synchronizedActionItems);
  // 两个都为空数组，直接返回空数组
  if (isCurrentRisksEmpty && isSynchronizedRisksEmpty) return [];
  // 当前Updates的Action Items为空数组，则返回继承的Action Items数据
  if (isCurrentRisksEmpty) return synchronizedActionItems as IActionItemVO[];
  // 继承的Action Items为空数组，则返回当前Updates的Action Items数据
  if (isSynchronizedRisksEmpty) return currentActionItems as IActionItemVO[];

  if (synchronizedActionItems!.length > 0 && getIsInitialActionItems(currentActionItems)) {
    return uniqActionItems(synchronizedActionItems);
  }

  // 继承的Updates的Action Items的comment映射（记录继承的Updates的Action Items中存在哪些comment）
  const synchronizedActionItemsComments = synchronizedActionItems!.reduce<IRecord<true>>((acc, cur) => {
    const { comment } = cur || {};
    if (!comment) return acc;
    return { ...acc, [comment]: true };
  }, {});

  // 当前updates的Action Items数据过滤掉继承的Updates的Action Items中存在的comment的Action Items数据
  const currentActionItemsFiltered = currentActionItems!.filter((currentActionItem) => {
    const { comment } = currentActionItem || {};
    return !synchronizedActionItemsComments[comment];
  });

  return uniqActionItems([...(synchronizedActionItems as IActionItemVO[]), ...currentActionItemsFiltered]);
};

const getRiskUniqueKey = (risk: IRiskVO): string => {
  const { category, rating, comment } = risk || {};
  return `${category}__${rating}__${comment}`;
};

/**
 * 获取组合后的Risks
 * @param currentRisks 当前Updates的Risks
 * @param synchronizedRisks 当前Updates的继承的Risks
 * @returns 组合后的Risks
 */
export const composeRisks = (currentRisks?: IRiskVO[] | null, synchronizedRisks?: IRiskVO[] | null): IRiskVO[] => {
  if (!currentRisks && !synchronizedRisks) return [];
  const isCurrentRisksEmpty = isEmptyList(currentRisks);
  const isSynchronizedRisksEmpty = isEmptyList(synchronizedRisks);
  // 两个都为空数组，直接返回空数组
  if (isCurrentRisksEmpty && isSynchronizedRisksEmpty) return [];
  // 当前Updates的Risks为空数组，则返回继承的Risks数据
  if (isCurrentRisksEmpty) return synchronizedRisks as IRiskVO[];
  // 继承的Risks为空数组，则返回当前Updates的Risks数据
  if (isSynchronizedRisksEmpty) return currentRisks as IRiskVO[];

  if (synchronizedRisks!.length > 0 && getIsInitialList(currentRisks)) {
    return uniqBy(synchronizedRisks, getRiskUniqueKey);
  }

  return uniqBy([...(synchronizedRisks as IRiskVO[]), ...(currentRisks as IRiskVO[])], getRiskUniqueKey);
};
